import React, { useEffect, useRef, useState } from "react";

interface Address {
  street: string;
  area: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
}

interface GooglePlacesAutocompleteProps {
  value: string; // Formik field for street
  onChange: (val: string) => void; // Formik onChange
  onAddressSelect: (address: Address) => void; // Full address
  placeholder?: string;
}

const loadGoogleMaps = (apiKey: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if ((window as any).google) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject("Google Maps script failed to load");
    document.head.appendChild(script);
  });
};

const parseAddressComponents = (
  components: google.maps.GeocoderAddressComponent[]
): Address => {
  const address: Address = {
    street: "",
    area: "",
    city: "",
    state: "",
    postcode: "",
    country: "",
  };

  components.forEach((comp) => {
    const types = comp.types;

    if (types.includes("street_number")) {
      address.street = comp.long_name + " " + address.street;
    }
    if (types.includes("route")) {
      address.street += comp.long_name;
    }

    if (
      types.includes("sublocality") ||
      types.includes("sublocality_level_1") ||
      types.includes("neighborhood")
    ) {
      address.area = comp.long_name;
    }

    // City can be locality, sublocality_level_1, or postal_town (UK)
    if (
      types.includes("locality") ||
      types.includes("sublocality_level_1") ||
      types.includes("postal_town")
    ) {
      address.city = comp.long_name;
    }

    if (types.includes("administrative_area_level_1")) {
      address.state = comp.long_name;
    }

    if (types.includes("postal_code")) {
      address.postcode = comp.long_name;
    }

    if (types.includes("country")) {
      address.country = comp.long_name;
    }
  });

  return address;
};

const GooglePlacesAutocomplete: React.FC<GooglePlacesAutocompleteProps> = ({
  value,
  onChange,
  onAddressSelect,
  placeholder = "Start typing your address...",
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete>();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
    loadGoogleMaps(apiKey)
      .then(() => setLoaded(true))
      .catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    if (!loaded || !inputRef.current) return;

    autocompleteRef.current = new window.google.maps.places.Autocomplete(
      inputRef.current,
      {
        types: ["address"],
        componentRestrictions: {
          country: ["US", "GB", "IN", "AU", "CA", "KE", "UG", "TZ"],
        },
      }
    );

    autocompleteRef.current.addListener("place_changed", () => {
      const place = autocompleteRef.current!.getPlace();
      if (!place.address_components) return;

      const address = parseAddressComponents(place.address_components);

      onAddressSelect(address);
    });
  }, [loaded]);

  return (
    <input
      ref={inputRef}
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="form-control form-control-solid form-control-lg"
      autoComplete="off"
    />
  );
};

export default GooglePlacesAutocomplete;
